import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { adminFetch } from '@/lib/api';
import { useEffect, useMemo, useState } from 'react';

type BillingClient = {
  id: number;
  legal_name: string;
  trade_name: string | null;
  billing_email: string;
  plan_code: string;
  status: string;
  stripe_status: string | null;
  plan_expires_at: string | null;
  is_paying: boolean;
  total_paid_cents: number;
  last_paid_at: string | null;
  notes?: string | null;
  stripe_customer_id?: string | null;
};

type ClientDetail = {
  client: BillingClient & {
    document: string;
    notes: string | null;
    stripe_customer_id: string | null;
    stripe_subscription_id: string | null;
  };
  plans: Array<{
    id: number;
    plan_code: string;
    monthly_value: string | number | null;
    started_at: string;
    ended_at: string | null;
  }>;
  invoices: Array<{
    id: number;
    stripe_invoice_id: string;
    amount_cents: number;
    currency: string;
    status: string;
    paid_at: string | null;
    hosted_invoice_url: string | null;
  }>;
};

function brl(cents: number) {
  return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function BillingPage() {
  const [params] = useSearchParams();
  const pastDue = params.get('past_due') === '1';
  const trials = params.get('trials') === '1';
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const qs = useMemo(() => {
    const q = new URLSearchParams();
    if (pastDue) q.set('past_due_only', '1');
    if (trials) q.set('trials_expiring', '1');
    const s = q.toString();
    return s ? `?${s}` : '';
  }, [pastDue, trials]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['billing-clients', qs],
    queryFn: () => adminFetch<{ clients: BillingClient[] }>(`/billing/clients${qs}`),
  });

  const clients = data?.clients ?? [];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold">Assinaturas</h1>
        <p className="text-sm text-neutral-400">
          Quem está pagando, total pago e falhas de cobrança
          {pastDue ? ' · filtrando past_due' : ''}
          {trials ? ' · trials a vencer' : ''}
        </p>
      </div>

      {isLoading && <p className="text-neutral-400">Carregando…</p>}
      {error && <p className="text-red-400">{error instanceof Error ? error.message : 'Erro'}</p>}

      <div className="grid gap-4 lg:grid-cols-5">
        <div className="lg:col-span-3 overflow-hidden rounded-xl border border-neutral-800">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-neutral-900 text-neutral-400">
              <tr>
                <th className="px-3 py-2 font-medium">Cliente</th>
                <th className="px-3 py-2 font-medium">Plano</th>
                <th className="px-3 py-2 font-medium">Stripe</th>
                <th className="px-3 py-2 font-medium">Total pago</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => (
                <tr
                  key={c.id}
                  className={`cursor-pointer border-t border-neutral-800 hover:bg-neutral-900/60 ${
                    selectedId === c.id ? 'bg-neutral-900' : ''
                  }`}
                  onClick={() => setSelectedId(c.id)}
                >
                  <td className="px-3 py-2">
                    <div className="font-medium">{c.trade_name || c.legal_name}</div>
                    <div className="text-xs text-neutral-500">{c.billing_email}</div>
                  </td>
                  <td className="px-3 py-2">{c.plan_code}</td>
                  <td className="px-3 py-2">
                    <span className={c.is_paying ? 'text-emerald-400' : 'text-neutral-400'}>
                      {c.stripe_status ?? '—'}
                    </span>
                  </td>
                  <td className="px-3 py-2">{brl(c.total_paid_cents)}</td>
                </tr>
              ))}
              {clients.length === 0 && !isLoading && (
                <tr>
                  <td colSpan={4} className="px-3 py-8 text-center text-neutral-500">
                    Nenhum cliente neste filtro.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="lg:col-span-2">
          {selectedId ? (
            <ClientBillingDetail clientId={selectedId} />
          ) : (
            <div className="rounded-xl border border-neutral-800 p-4 text-sm text-neutral-500">
              Selecione um cliente para ver invoices e editar plano/status.
            </div>
          )}
        </div>
      </div>

      <p className="text-xs text-neutral-500">
        Módulos ML/vendas:{' '}
        <Link to="/clients" className="text-sky-400 hover:underline">
          página Clientes
        </Link>
      </p>
    </div>
  );
}

function ClientBillingDetail({ clientId }: { clientId: number }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['billing-client', clientId],
    queryFn: () => adminFetch<ClientDetail>(`/billing/clients/${clientId}`),
  });

  const [planCode, setPlanCode] = useState('trial');
  const [status, setStatus] = useState('active');
  const [expires, setExpires] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!data?.client) return;
    setPlanCode(data.client.plan_code);
    setStatus(data.client.status);
    setExpires(data.client.plan_expires_at ?? '');
    setNotes(data.client.notes ?? '');
  }, [data?.client]);

  const update = useMutation({
    mutationFn: () =>
      adminFetch(`/billing/clients/${clientId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          plan_code: planCode,
          status,
          plan_expires_at: expires || null,
          notes,
        }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['billing-client', clientId] });
      void qc.invalidateQueries({ queryKey: ['billing-clients'] });
      void qc.invalidateQueries({ queryKey: ['billing-overview'] });
    },
  });

  if (isLoading || !data) return <p className="text-sm text-neutral-400">Carregando detalhe…</p>;

  const c = data.client;

  return (
    <div className="space-y-4 rounded-xl border border-neutral-800 p-4">
      <div>
        <h2 className="font-semibold">{c.trade_name || c.legal_name}</h2>
        <p className="text-xs text-neutral-500">
          #{c.id} · {c.billing_email}
        </p>
        <p className="mt-1 text-xs text-neutral-500">
          Total pago: <strong>{brl(c.total_paid_cents)}</strong>
          {c.stripe_customer_id ? ` · ${c.stripe_customer_id}` : ''}
        </p>
      </div>

      <div className="grid gap-2 text-sm">
        <label className="space-y-1">
          <span className="text-xs text-neutral-400">Plano</span>
          <select
            className="w-full rounded border border-neutral-700 bg-neutral-900 px-2 py-1.5"
            value={planCode}
            onChange={(e) => setPlanCode(e.target.value)}
          >
            <option value="trial">trial</option>
            <option value="essencial">essencial</option>
            <option value="profissional">profissional</option>
          </select>
        </label>
        <label className="space-y-1">
          <span className="text-xs text-neutral-400">Status conta</span>
          <select
            className="w-full rounded border border-neutral-700 bg-neutral-900 px-2 py-1.5"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="active">active</option>
            <option value="suspended">suspended</option>
            <option value="cancelled">cancelled</option>
          </select>
        </label>
        <label className="space-y-1">
          <span className="text-xs text-neutral-400">Expira em</span>
          <input
            type="date"
            className="w-full rounded border border-neutral-700 bg-neutral-900 px-2 py-1.5"
            value={expires}
            onChange={(e) => setExpires(e.target.value)}
          />
        </label>
        <label className="space-y-1">
          <span className="text-xs text-neutral-400">Notas</span>
          <textarea
            className="w-full rounded border border-neutral-700 bg-neutral-900 px-2 py-1.5 text-xs"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </label>
        <button
          type="button"
          disabled={update.isPending}
          onClick={() => update.mutate()}
          className="rounded-lg bg-sky-700 px-3 py-2 text-sm font-medium hover:bg-sky-600 disabled:opacity-50"
        >
          Salvar override
        </button>
        {update.isError && (
          <p className="text-xs text-red-400">
            {update.error instanceof Error ? update.error.message : 'Erro ao salvar'}
          </p>
        )}
        {update.isSuccess && <p className="text-xs text-emerald-400">Salvo.</p>}
      </div>

      <div>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-500">
          Invoices
        </h3>
        <ul className="max-h-48 space-y-1 overflow-y-auto text-xs">
          {data.invoices.map((inv) => (
            <li key={inv.id} className="flex justify-between gap-2 border-t border-neutral-800 py-1">
              <span>
                {inv.status} · {brl(inv.amount_cents)}
                {inv.paid_at ? ` · ${new Date(inv.paid_at).toLocaleDateString('pt-BR')}` : ''}
              </span>
              {inv.hosted_invoice_url && (
                <a
                  href={inv.hosted_invoice_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sky-400"
                >
                  ver
                </a>
              )}
            </li>
          ))}
          {data.invoices.length === 0 && <li className="text-neutral-500">Nenhuma invoice.</li>}
        </ul>
      </div>
    </div>
  );
}
